import * as React from 'react'

import { cn } from '@/utils'
import { Input, type InputProps } from './input'

export interface InputGroupProps extends React.ComponentProps<'div'> {
  children: React.ReactNode
}

const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('relative', className)} {...props}>
        {children}
      </div>
    )
  }
)

InputGroup.displayName = 'InputGroup'

export interface InputGroupPrefixProps extends React.ComponentProps<'div'> {
  children: React.ReactNode
}

const InputGroupPrefix = React.forwardRef<HTMLDivElement, InputGroupPrefixProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none', className)}
        {...props}
      >
        {children}
      </div>
    )
  }
)

InputGroupPrefix.displayName = 'InputGroupPrefix'

export interface InputGroupSuffixProps extends React.ComponentProps<'div'> {
  children: React.ReactNode
}

const InputGroupSuffix = React.forwardRef<HTMLDivElement, InputGroupSuffixProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none', className)}
        {...props}
      >
        {children}
      </div>
    )
  }
)

InputGroupSuffix.displayName = 'InputGroupSuffix'

// Helper component that combines Input with suffix
export interface InputWithSuffixProps extends InputProps {
  suffix?: React.ReactNode
}

const InputWithSuffix = React.forwardRef<HTMLInputElement, InputWithSuffixProps>(
  ({ suffix, className, size, ...props }, ref) => {
    if (!suffix) {
      return <Input ref={ref} className={className} size={size} {...props} />
    }

    return (
      <InputGroup>
        <Input ref={ref} className={className} size={size} {...props} />
        <InputGroupSuffix>{suffix}</InputGroupSuffix>
      </InputGroup>
    )
  }
)

InputWithSuffix.displayName = 'InputWithSuffix'

export { InputGroup, InputGroupPrefix, InputGroupSuffix, InputWithSuffix }
